/**
 * @file
 * Page Header component behaviors.
 */

(function (Drupal, once) {
  'use strict';

  function toggleMenus(items, activeIdx, forceActive = null) {
    items.forEach((item, idx) => {
      const button = item.querySelector('[aria-controls]');

      if (!button) {
        return;
      }

      let isActive = forceActive && idx === activeIdx;
      if (forceActive === null) {
        const alreadyActive = button.getAttribute('aria-expanded') === 'true';
        isActive = activeIdx === idx ? !alreadyActive : false;
      }
      button.setAttribute('aria-expanded', isActive);
      const menu = document.getElementById(
        button.getAttribute('aria-controls')
      );
      if (menu) {
        menu.classList.toggle('submenu--expanded', isActive);
      }
    });
  }

  Drupal.behaviors.pageHeader = {
    attach: function (context, settings) {
      // Use once to ensure the behavior is only attached once per element
      const pageHeaders = once('page-header', '.page-header', context);

      const header = document.querySelector('.page-header');
      if (!header) {
        return;
      }

      let lastScrollY = window.scrollY;
      let ticking = false;

      const updateHeaderOnScroll = () => {
        const currentScrollY = window.scrollY;
        const viewportHeight = window.innerHeight;
        const scrollThreshold = viewportHeight * 0.5;

        const isScrollingDown = currentScrollY > lastScrollY;
        const isPastThreshold = currentScrollY > scrollThreshold;

        // Set a CSS variable for the current header offset.
        document.documentElement.style.setProperty('--header-current-offset', `${header.offsetTop}px`);

        if (isPastThreshold) {
          if (isScrollingDown) {
            document.body.classList.add('header-hidden');
            document.body.classList.remove('header-sticky', 'header-static');
          }
          else if (lastScrollY - currentScrollY > 10) {
            // Hysteresis
            document.body.classList.add('header-sticky');
            document.body.classList.remove('header-hidden', 'header-static');
          }
        }
        else {
          document.body.classList.add('header-static');
          document.body.classList.remove('header-hidden', 'header-sticky');
        }

        lastScrollY = currentScrollY <= 0 ? 0 : currentScrollY;
        ticking = false;
      };

      const onScroll = () => {
        if (!ticking) {
          window.requestAnimationFrame(updateHeaderOnScroll);
          ticking = true;
        }
      };

      window.addEventListener('scroll', onScroll, {passive: true});

      // Set initial state on load
      updateHeaderOnScroll();

      pageHeaders.forEach(function (pageHeader) {
        // Mobile menu toggle functionality
        const mobileToggle = pageHeader.querySelector('.page-header__mobile-toggle');
        const navigation = pageHeader.querySelector('.page-header__navigation');
        const menuItems = navigation ? navigation.querySelectorAll('.main-menu li') : [];

        if (mobileToggle && navigation) {
          mobileToggle.addEventListener('click', function () {
            const expanded =
              mobileToggle.getAttribute('aria-expanded') === 'true';
            mobileToggle.setAttribute('aria-expanded', String(!expanded));
          });
        }

        const mobileBehavior = event => {
          // Click outside
          if (!navigation.contains(event.target)) {
            toggleMenus(menuItems, false);
            return true;
          }

          // Check if the clicked element is a submenu toggle button
          const isSubmenuButton = event.target.hasAttribute('aria-controls') || event.target.closest('[aria-controls]');

          // Only prevent default for submenu toggles, not regular links
          if (isSubmenuButton) {
            event.preventDefault();
            // Close all other submenus
            toggleMenus(menuItems, Array.from(menuItems).indexOf(event.target.closest('li')));
            return false;
          }

          // Allow regular links to work normally
          return true;
        };

        const debounce = (callback, wait) => {
          let timeoutId = null;

          const debouncedFunction = (...params) => {
            window.clearTimeout(timeoutId);
            timeoutId = window.setTimeout(() => {
              callback(...params);
            }, wait);
          };

          debouncedFunction.cancel = () => {
            window.clearTimeout(timeoutId);
          };

          return debouncedFunction;
        };

        const handleMouseLeave = debounce(toggleMenus.bind(this, menuItems, false), 240);

        const setupDesktop = () => {
          navigation.addEventListener('mouseleave', handleMouseLeave);
          navigation.addEventListener('mouseenter', handleMouseLeave.cancel);

          // Track hover state for each menu
          return Array.from(menuItems).map((item, idx) => {
            const fn = (event) => {
              const isExpanded = event.target.firstElementChild.getAttribute('aria-expanded') === 'true';

              if (!isExpanded) {
                toggleMenus(menuItems, idx);
              }
            };

            item.addEventListener('mouseenter', fn);
            return () => {
              item.removeEventListener('mouseenter', fn);
            };
          });
        };

        const breakpointValue = window
          .getComputedStyle(document.documentElement)
          .getPropertyValue('--breakpoint-tablet');
        if (!breakpointValue) {
          return;
        }
        let listeners = [];
        const breakpoint = window.matchMedia(`(min-width: ${breakpointValue})`);

        const applyResponsiveBehavior = () => {
          if (!breakpoint.matches) {
            navigation.addEventListener('click', mobileBehavior);
            listeners.forEach(unlisten => {
              unlisten();
            });
            listeners = [];
            return;
          }

          navigation.removeEventListener('click', mobileBehavior);
          listeners = setupDesktop();
        };

        breakpoint.addEventListener('change', applyResponsiveBehavior);
        applyResponsiveBehavior();
      });
    }
  };

  Drupal.behaviors.pageHeaderSearch = {
    attach: function (context, settings) {
      once('js-page-header-search', '.header-search-form').forEach(function (component) {
        const searchButton = component.querySelector('button');

        const toggleSearchForm = () => {
          searchButton.setAttribute(
            'aria-expanded',
            searchButton.getAttribute('aria-expanded') === 'false' ? 'true' : 'false'
          );
        };

        searchButton.addEventListener('click', toggleSearchForm);
        searchButton.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            toggleSearchForm();
          }
        });
      });
    }
  };

  Drupal.behaviors.searchForm = {
    attach: function (context, settings) {
      once('js-search-form', 'form[action="/search"]').forEach(function (form) {
        const input = form.querySelector('[data-drupal-selector="edit-keywords"]');
        input.dataset.empty = input.value === '';

        // Create a button to clear keywords entry
        const clearButton = document.createElement('button');
        clearButton.type = 'button';
        clearButton.classList.add('input-clear-button');
        clearButton.setAttribute('aria-label', Drupal.t('Clear keywords field input'));

        input.after(clearButton);

        input.addEventListener('input', (event) => {
          event.target.dataset.empty = event.target.value === '';
        });

        clearButton.addEventListener('click', (event) => {
          input.value = '';
          input.dataset.empty = true;
          input.focus();
        });
      });
    }
  };
})(Drupal, once);
